import * as React from 'react';
import type { AnyFieldApi } from '@tanstack/react-form';
import { Field, FieldContent, FieldError, FieldLabelRow } from '@/components/ui/field';
import { cn } from '@/lib/utils';
import { useFormContext } from './FormProvider';
import { shouldShowFieldError, useFieldErrorVisibility } from './fieldErrorVisibility';

type Marker = 'required' | 'optional' | 'none';

export interface FormFieldRowProps {
  /** TanStack field name. */
  name: string;
  /** Resolved label text. */
  label: string;
  /**
   * Right-aligned marker on the label row. `'none'` also opts the field out of
   * the error treatment entirely — use it for an auto-derived / read-only field
   * (e.g. a time that Full day fills in). Defaults to `'required'`.
   */
  marker?: Marker;
  /** Resolved marker text (e.g. `t('…required')`); rendered unless marker is `'none'`. */
  markerLabel?: string;
  labelClassName?: string;
  /** Extra classes for the Field wrapper (e.g. grid spans). */
  className?: string;
  /**
   * Renders the control. Receives the TanStack field and the derived `invalid`
   * flag so it can paint its own red state (`aria-invalid` / an `invalid` prop).
   */
  children: (field: AnyFieldApi, state: { invalid: boolean }) => React.ReactNode;
}

function isEmpty(value: unknown): boolean {
  if (value == null || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

/**
 * The one field shell every modular form composes: label + Optional/Required
 * marker + §2 error timing + red invalid state, with the control supplied as a
 * render-prop child. A required-empty field is conveyed by the red state and the
 * marker alone; a message is shown only for a content error on a filled field
 * (format, cross-field), so bare "is required" strings never appear.
 */
export function FormFieldRow({
  name,
  label,
  marker = 'required',
  markerLabel,
  labelClassName,
  className,
  children,
}: FormFieldRowProps) {
  const { form } = useFormContext();
  const submitted = useFieldErrorVisibility();

  return (
    <form.Field name={name}>
      {(field) => {
        const invalid = marker !== 'none' && shouldShowFieldError(field.state.meta, submitted);
        const showMessage = invalid && !isEmpty(field.state.value) && field.state.meta.errors?.length > 0;
        return (
          <Field className={className} data-validation-error={invalid || undefined}>
            <FieldLabelRow
              label={label}
              marker={marker !== 'none' ? markerLabel : undefined}
              invalid={invalid}
              labelClassName={cn('font-inter text-sm font-semibold leading-normal text-grey', labelClassName)}
            />
            <FieldContent>
              {children(field, { invalid })}
              {showMessage && <FieldError errors={field.state.meta.errors} />}
            </FieldContent>
          </Field>
        );
      }}
    </form.Field>
  );
}
